import { useContext, useState } from 'react';

import {
	DeleteOutlined,
	EditOutlined,
	LoadingOutlined,
	StarFilled,
	StarOutlined,
} from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Drawer,
	Flex,
	Input,
	Popconfirm,
	Table,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import cn from 'classnames';

import {
	useDeleteQACollection,
	useGetAllQACollections,
	useUpdateQACollectionFavourite,
} from '@/api/batch_process';
import { useDebounce } from '@/hooks';
import prepareDate from '@/lib/helpers/prepareDate';
import { PageContext } from '@/providers/PageContextProvider';
import { IPagination } from '@/ts';

import styles from './styles.module.css';

type TProps = {
	onApplyQuestionData?: (data: any) => void;
};

export default function QADrawer({ onApplyQuestionData }: TProps) {
	const { drawer, closeDrawer } = useContext(PageContext);

	const [currentItem, setCurrentItem] = useState<number | null>(null);
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounce(search);
	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
	const [filterUserQuestions, setFilterUserQuestions] = useState(
		localStorage?.getItem('sv_show_user_questions') === '1' ? true : false,
	);
	const [onlyFavourites, setOnlyFavourites] = useState(
		localStorage?.getItem('sv_only_favourites_questions') === '1'
			? true
			: false,
	);

	const [form] = useForm();

	const { data, isLoading, isFetching } = useGetAllQACollections({
		page: pagination.page,
		limit: pagination.limit,
		enabled: drawer?.type === 'qa-history' && drawer?.isOpen,
		onlyUser: filterUserQuestions,
		favourites: onlyFavourites,
		search: debouncedSearch?.trim()?.length > 0 ? debouncedSearch : '',
		sortOrder,
	});

	const { mutate: deleteQACollection, isLoading: isDeleteLoading } =
		useDeleteQACollection();

	const { mutate: updateFavourite, isLoading: isUpdateLoading } =
		useUpdateQACollectionFavourite();

	const onCancelClick = () => {
		form.resetFields();
		closeDrawer();
	};

	const onTableChange = (__: any, _: any, sorter: any) => {
		const { order = 'ascend' } = sorter;

		if (order) {
			setSortOrder(order === 'ascend' ? 'asc' : 'desc');
		}
	};

	const onFavouriteChange = () => {
		setOnlyFavourites((prev) => {
			localStorage.setItem('sv_only_favourites_questions', !prev ? '1' : '0');
			return !prev;
		});
		setPagination((cur: any) => ({ ...cur, page: 1 }));
	};

	const onUserQuestionsChange = () => {
		setFilterUserQuestions((prev) => {
			localStorage.setItem('sv_show_user_questions', !prev ? '1' : '0');
			return !prev;
		});

		setPagination((cur: any) => ({ ...cur, page: 1 }));
	};

	const onFavouriteClick = (id: string, isFavourite: boolean) => {
		updateFavourite(
			{ id, favourite: !isFavourite },
			{
				onSuccess: () => {
					message.success(`Updated`);
				},
				onError: (err) => {
					message.error(
						`Error updating favourite status: ${
							(err as any)?.response?.data?.err || (err as any)?.message
						}`,
					);
				},
			},
		);
	};

	const items = data?.data?.data?.items || [];
	const total = data?.data?.data?.pagination?.total || 0;

	return (
		<Drawer
			open={drawer.isOpen && drawer.type === 'qa-history'}
			width={'650px'}
			onClose={onCancelClick}
			title="Question & Answer History"
			loading={isLoading || isFetching}
		>
			<Flex style={{ marginBottom: '1rem' }} justify="space-between" gap={16}>
				<Input
					placeholder="Search..."
					style={{ width: '300px' }}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<Flex justify="flex-end" gap={16} align="center">
					<Checkbox
						checked={filterUserQuestions}
						onChange={onUserQuestionsChange}
					>
						my questions
					</Checkbox>
					<Checkbox checked={onlyFavourites} onChange={onFavouriteChange}>
						favourites
					</Checkbox>
				</Flex>

				{/* <Typography.Title level={5} style={{ margin: 0 }}>
					Choose questions to ask about selected videos{' '}
				</Typography.Title>
				{items?.length > 0 && <small>found {items.length} question(s)</small>} */}
			</Flex>

			<Flex vertical gap={16} style={{ width: '100%' }}>
				<Table
					loading={isLoading || isFetching}
					dataSource={items}
					onChange={onTableChange}
					rowKey="id"
					pagination={{
						total,
						showSizeChanger: true,
						current: pagination.page,
						pageSizeOptions: [10, 25, 50],
						onChange: (page: number, pageSize: number) => {
							setPagination((cur) => ({ ...cur, page, limit: pageSize }));
						},
					}}
					bordered
					size="small"
					columns={[
						{
							title: 'Date',
							dataIndex: 'created_at',
							key: 'created_at',
							width: 100,
							sorter: true,
							render: (text) => (
								<>{text ? prepareDate('' + text) : 'no date'}</>
							),
						},
						{
							title: 'Title',
							dataIndex: 'title',
							key: 'title',
							width: 150,
							render: (text) => <Typography.Text>{text}</Typography.Text>,
						},
						{
							title: 'Question',
							dataIndex: 'question',
							key: 'question',
							render: (text) => <Typography.Text>{text}</Typography.Text>,
						},
						{
							title: 'Actions',
							dataIndex: 'actions',
							key: 'actions',
							width: 120,
							render: (_, record: any) => (
								<Flex
									gap={8}
									style={{ width: '100%' }}
									align="center"
									justify="center"
								>
									<Tooltip title="Use question" mouseEnterDelay={1}>
										<Button
											size="middle"
											style={{
												fontSize: '18px',
												padding: '6px',
												height: 'unset',
											}}
											disabled={isDeleteLoading}
											onClick={() => {
												if (onApplyQuestionData) {
													onApplyQuestionData(record);
												}
											}}
										>
											<EditOutlined />
										</Button>
									</Tooltip>

									<Tooltip
										title={
											record?.favourite
												? 'Remove from favourites'
												: 'Add to favourites'
										}
										mouseEnterDelay={1}
									>
										<Button
											size="middle"
											onClick={() => {
												setCurrentItem(record?.id);
												onFavouriteClick(record?.id, record?.favourite);
											}}
											style={{
												fontSize: '18px',
												padding: '6px',
												height: 'unset',
											}}
											className={cn(
												styles.starHover,
												record?.favourite && styles.starHoverActive,
											)}
											disabled={
												isUpdateLoading ||
												isDeleteLoading ||
												record?.is_user_collection === false
											}
										>
											{isUpdateLoading && currentItem === record?.id ? (
												<LoadingOutlined />
											) : record?.favourite ? (
												<StarFilled />
											) : (
												<StarOutlined />
											)}
										</Button>
									</Tooltip>

									<Tooltip title="Delete question">
										<Popconfirm
											title="Are you sure you want to delete this question?"
											onConfirm={() => {
												setCurrentItem(record?.id);
												deleteQACollection(record?.id, {
													onSuccess: () => {
														message.success('Question deleted successfully');
														setPagination((cur) => ({
															...cur,
															page: 1,
														}));
														setCurrentItem(null);
													},
													onError: (err) => {
														message.error(
															`Error deleting question: ${
																(err as any)?.response?.data?.err ||
																(err as any)?.message
															}`,
														);
														setCurrentItem(null);
													},
												});
											}}
											okText="Yes"
											cancelText="Cancel"
										>
											<Button
												size="small"
												danger
												style={{
													fontSize: '18px',
													padding: '6px',
													height: 'unset',
												}}
												loading={isDeleteLoading && currentItem === record?.id}
												disabled={
													isDeleteLoading ||
													record?.is_user_collection === false
												}
											>
												<DeleteOutlined />
											</Button>
										</Popconfirm>
									</Tooltip>
								</Flex>
							),
						},
					]}
				/>
			</Flex>
		</Drawer>
	);
}
